// ========================================
// JUDGY DESIGN SYSTEM
// ========================================

// Base Colors
export const colors = {
  charcoal: '#1F1F1F',
  orange: '#FF7A00',
  amber: '#FFC267',
  offWhite: '#F9F7F4',
  warmGray: '#E5E1DC',
  emerald: '#00C896',
  coral: '#FF4F4F',
};

// These define screen sizes for mobile, tablet, and desktop
export const breakpoints = {
  mobile: '480px',      // Small phones
  tablet: '768px',      // Tablets and large phones
  desktop: '1024px',    // Desktop and laptops
  wide: '1440px'        // Large desktop screens
};

// ← ADD: Media Query Helper Functions
// Use these to apply styles only at certain screen sizes
export const mediaQueries = {
  mobile: `@media (max-width: ${breakpoints.mobile})`,
  tablet: `@media (max-width: ${breakpoints.tablet})`,
  desktop: `@media (min-width: ${breakpoints.desktop})`,
  
  // Helper for touch devices
  touch: '@media (hover: none) and (pointer: coarse)',
};

// Semantic Theme
export const theme = {
  // Backgrounds
  bg: {
    primary: colors.offWhite,
    secondary: colors.warmGray,
    dark: colors.charcoal,
  },
  
  // Text
  text: {
    primary: colors.charcoal,
    secondary: '#666666',
    inverse: colors.offWhite,
    muted: '#999999',
  },
  
  // Brand
  brand: {
    primary: colors.orange,
    primaryHover: colors.amber,
    secondary: colors.amber,
  },
  
  // Status
  status: {
    success: colors.emerald,
    error: colors.coral,
    info: colors.orange,
  },
  
  // Borders
  border: {
    light: colors.warmGray,
    medium: '#CCCCCC',
    dark: colors.charcoal,
  },
  
  // Effects
  overlay: 'rgba(31, 31, 31, 0.5)',
  shadow: {
    small: '0 2px 4px rgba(0, 0, 0, 0.1)',
    medium: '0 4px 12px rgba(0, 0, 0, 0.15)',
    large: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
};

// Typography
export const typography = {
  fontFamily: {
    primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'Monaco, Consolas, "Courier New", monospace',
  },
  
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
  },

  // ADD: Mobile font sizes (slightly smaller for readability on small screens)
  mobileFontSize: {
    xs: '11px',
    sm: '13px',
    base: '15px',
    lg: '17px',
    xl: '19px',
    '2xl': '22px',
    '3xl': '26px',
    '4xl': '32px',
  },
  
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// Spacing (use for padding, margin, gaps)
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px',
  '4xl': '40px',
};

// Mobile-optimized spacing (smaller for mobile screens)
export const mobileSpacing = {
  xs: '2px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  '3xl': '24px',
  '4xl': '32px',
};

// Border Radius
export const borderRadius = {
  none: '0',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  full: '9999px', // For pills/circles
};

// ========================================
// COMPONENT STYLES (Reusable)
// ========================================

// Button variants
export const buttonStyles = {
  // Base button (shared properties)
  base: {
    padding: `${spacing.md} ${spacing.xl}`,
    border: 'none',
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: typography.fontFamily.primary,
    minHeight: '44px', // Apple's recommended touch target size
    minWidth: '44px'
  },

  // Mobile specific button style
  baseMobile: {
    padding: `${mobileSpacing.md} ${mobileSpacing.lg}`,
    fontSize: typography.mobileFontSize.base,
    minHeight: '48px', // Slightly larger touch target for mobile
  },
  
  // Primary button (main CTAs)
  primary: {
    backgroundColor: theme.brand.primary,
    color: theme.text.inverse,
  },
  primaryHover: {
    backgroundColor: theme.brand.primaryHover,
  },
  
  // Secondary button (less prominent actions)
  secondary: {
    backgroundColor: 'transparent',
    color: theme.text.primary,
    border: `2px solid ${theme.border.light}`,
  },
  secondaryHover: {
    backgroundColor: theme.bg.secondary,
  },
  
  // Danger button (destructive actions)
  danger: {
    backgroundColor: theme.status.error,
    color: theme.text.inverse,
  },
  dangerHover: {
    backgroundColor: '#FF3333',
  },
  
  // Dark button (for light backgrounds)
  dark: {
    backgroundColor: theme.bg.dark,
    color: theme.text.inverse,
  },
  darkHover: {
    backgroundColor: '#2A2A2A',
  },
  
  // Disabled state
  disabled: {
    backgroundColor: '#CCCCCC',
    color: '#888888',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  
  // Small size variant
  small: {
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.fontSize.sm,
  },
  
  // Large size variant
  large: {
    padding: `${spacing.lg} ${spacing['2xl']}`,
    fontSize: typography.fontSize.lg,
  },
};

// Input styles
export const inputStyles = {
  base: {
    width: '100%',
    padding: spacing.md,
    border: `2px solid ${theme.border.light}`,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.primary,
    backgroundColor: 'white',
    color: theme.text.primary,
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box',
    WebKitAppearance: 'none', // Removes iOS styling
    minHeight: '44px', // Touch-friendly height
  },

  baseMobile: {
    padding: mobileSpacing.md,
    fontSize: typography.mobileFontSize.base,
    minHeight: '48px', // Larger touch target for mobile
  },
  
  focus: {
    borderColor: theme.brand.primary,
    outline: 'none',
  },
  
  error: {
    borderColor: theme.status.error,
  },
};

// Card/Panel styles
export const cardStyles = {
  base: {
    backgroundColor: theme.bg.secondary,
    borderRadius: borderRadius.lg,
    padding: spacing['2xl'],
    boxShadow: theme.shadow.medium,
  },
  
  elevated: {
    backgroundColor: 'white',
    borderRadius: borderRadius.lg,
    padding: spacing['2xl'],
    boxShadow: theme.shadow.large,
  },
};

// Modal styles
export const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.overlay,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    overflowY: 'auto',
  },
  
  content: {
    backgroundColor: theme.bg.primary,
    borderRadius: borderRadius.xl,
    padding: spacing['3xl'],
    maxWidth: '450px',
    width: '90%',
    boxShadow: theme.shadow.large,
    position: 'relative',
    maxHeight: '90vh',
    overflowY: 'auto',
  },

  contentMobile: {
    padding: mobileSpacing.xl,
    width: '95%',
    maxHeight: '95vh',
    borderRadius: borderRadius.lg,
  },
  
  closeButton: {
    position: 'absolute',
    top: spacing.lg,
    right: spacing.lg,
    background: 'none',
    border: 'none',
    fontSize: typography.fontSize['2xl'],
    cursor: 'pointer',
    color: theme.text.primary,
    lineHeight: 1,
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

// Message bubble styles
export const messageBubbleStyles = {
  user: {
    backgroundColor: theme.brand.primary,
    color: theme.text.inverse,
    borderRadius: `${borderRadius.lg} ${borderRadius.lg} ${borderRadius.sm} ${borderRadius.lg}`,
    padding: `${spacing.md} ${spacing.lg}`,
    maxWidth: '70%',
    wordWrap: 'break-word',
  },

  userMobile: {
    padding: `${mobileSpacing.sm} ${mobileSpacing.md}`,
    fontSize: typography.mobileFontSize.base,
    maxWidth: '85%', // Wider on mobile since screen is smaller
  },
  
  bot: {
    backgroundColor: theme.bg.secondary,
    color: theme.text.primary,
    borderRadius: `${borderRadius.lg} ${borderRadius.lg} ${borderRadius.lg} ${borderRadius.sm}`,
    padding: `${spacing.md} ${spacing.lg}`,
    maxWidth: '80%', // Bot messages can be slightly wider
    wordWrap: 'break-word',
  },

  botMobile: {
    padding: `${mobileSpacing.sm} ${mobileSpacing.md}`,
    fontSize: typography.mobileFontSize.base,
    maxWidth: '90%', // Wider on mobile
  },
};

// Helper function to merge styles (like spreading multiple style objects)
export const mergeStyles = (...styles) => {
  return Object.assign({}, ...styles);
};

// Helper to check if we're on mobile
// Use this in your components to conditionally apply mobile styles
export const isMobile = () => {
  return window.innerWidth <= parseInt(breakpoints.tablet);
};

// Hook for responsive behavior (optional - can use in components)
// This creates a CSS string for media queries
export const createMediaQuery = (breakpoint, styles) => {
  return `@media (max-width: ${breakpoint}) {
    ${Object.entries(styles).map(([key, value]) => 
      `${key}: ${value};`
    ).join('\n    ')}
  }`;
};